import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import { Tabs, TabList, Tab, TabPanel, TabPanels, Spinner } from '@pearsontechnology/aero-components';

import {
  commandWorkspaceWorkspacesList,
  commandWorkspaceGrantPermission,
  commandIamAccessRequestList,
} from 'containers/ConnectionManager/actions';
import provisionAccount from 'containers/ConnectionManager/provisionAccount';

import {
  PageWrapper,
  PageViewport,
  PageContent,
} from 'components/PageStyles';

import Sidebar from 'Workspace/components/WorkspaceSidebar';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';

import { selectWorkspacesList, selectRequestAccessRequests, selectToDoTabLoaded, selectCompleteTabLoaded } from 'Workspace/Admin/store/selectors';
import { setRequestAccessLoaded } from 'Workspace/Admin/store/actions';

import AdminNavigation from '../AdminNavigation';
import RequestAccessList from './components/RequestAccessList';
import ApproveRequestAccessModal from './components/ApproveRequestAccessModal';
import { TabPanelEmptyState } from './styles';
import { STATUS_REVIEW, STATUS_COMPLETE, STATUS_DENY, REQUEST_ACCESS_STATUS_VALUES } from './constants';

import messages from './messages';
import authenticationTypes from 'constants/authenticationTypes';
import { UserRoles } from 'constants/enums';

const normalizeRequestItem = (item) => {
  if (!item) return item;
  if (item.accessRequest) {
    return {
      ...item.accessRequest,
      email: item.profile?.primaryEmail ?? item.profile?.email ?? item.accessRequest?.email,
    };
  }
  return item;
};

const AdminRequestAccess = ({ requests, toDoTabLoaded, completeTabLoaded, getWorkspaces, getRequestAccessList, workspacesList, grantPermission }) => {
  const [requestAccessList, setRequestAccessList] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approvedRequest, setApprovedRequest] = useState(null);
  const [approvingRequestId, setApprovingRequestId] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState({});
  const [newAccountId, setNewAccountId] = useState(null); // when user is provisioned, they are given an accountId
  
  useEffect(() => {
    getWorkspaces();
    getRequestAccessList();
  }, [getWorkspaces, getRequestAccessList]);

  useEffect(() => {
    const collated = (requests && requests.length) ? requests : [];
    setRequestAccessList(collated);
  }, [requests]);

  const rawList = (requestAccessList && requestAccessList.length) ? requestAccessList : [];
  const baseRequests = rawList.map(normalizeRequestItem);
  const allRequests = baseRequests.map((r) => ({
    ...r,
    status: statusUpdates[r.pearsonId] !== undefined ? statusUpdates[r.pearsonId] : (r.status || STATUS_REVIEW),
  }));
  const toDoRequests = allRequests.filter((r) => (r.status || STATUS_REVIEW) === STATUS_REVIEW);
  const completeRequests = allRequests.filter((r) => r.status === STATUS_COMPLETE || r.status === STATUS_DENY);
  const createUser = async (request) => {
    const email = request?.email ?? request?.profile?.primaryEmail ?? request?.profile?.email;
    if (!email) return null;
    const account = {
      pearsonId: request.pearsonId,
      region: request.region || 'US',
      authenticationType: authenticationTypes.IES_V2,
      email,
      password: '', // Per Jimmy, ignored when authenticationType is IES_V2
      roles: [UserRoles.AERO_INSTRUCTOR],
    };
    const response = await provisionAccount(account);
    if (response?.accountId) {
      setNewAccountId(response.accountId);
    }
    return response;
  };

  const onApprove = async (request) => {
    setApprovingRequestId(request.pearsonId);
    try {
      await createUser(request);
      setApprovedRequest(request);
      setApproveModalVisible(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('createUser failed', err);
      // Toast is shown by provisionAccount
    } finally {
      setApprovingRequestId(null);
    }
  };

  const onDismissApproveModal = () => {
    setApproveModalVisible(false);
    setApprovedRequest(null);
  };

  const onFinalizeApprove = (request, workspaceIds, permissionLevel) => {
    if (newAccountId && workspaceIds?.length && permissionLevel) {
      workspaceIds.forEach((workspaceId) => {
        grantPermission(newAccountId, 'account', workspaceId, permissionLevel);
      });
    }
    if (request?.pearsonId) {
      setStatusUpdates((prev) => ({ ...prev, [request.pearsonId]: STATUS_COMPLETE }));
    }
    onDismissApproveModal();
  };

  const onDeny = (request) => {
    if (request?.pearsonId) {
      setStatusUpdates((prev) => ({ ...prev, [request.pearsonId]: STATUS_DENY }));
    }
  };

  const tabData = [
    {
      tabHeader: intl.formatMessage(messages.tabToDo),
      tabPanelContent: toDoRequests.length ? (
        <RequestAccessList
          requests={toDoRequests}
          showActions
          onApprove={onApprove}
          onDeny={onDeny}
          approvingRequestId={approvingRequestId}
        />
      ) : (
        <TabPanelEmptyState>
          {!toDoTabLoaded ? <Spinner /> : intl.formatMessage(messages.noRequests)}
        </TabPanelEmptyState>
      ),
    },
    {
      tabHeader: intl.formatMessage(messages.tabComplete),
      tabPanelContent: completeRequests.length ? (
        <RequestAccessList requests={completeRequests} showActions={false} showStatusColumn />
      ) : (
        <TabPanelEmptyState>
          {!completeTabLoaded ? <Spinner /> : intl.formatMessage(messages.noCompletedRequests)}
        </TabPanelEmptyState>
      ),
    },
  ];

  return (
    <PageWrapper>
      <Sidebar navigation={<AdminNavigation selectedKey="requestAccess" />} />
      <PageViewport>
        <WorkspaceHeader title={<FormattedMessage {...messages.title} />} />
        <PageContent>
          <Tabs selectedIndex={selectedTabIndex} selectTab={(index) => setSelectedTabIndex(index)}>
            <TabList>
              {tabData.map((item) => <Tab key={item.tabHeader}>{item.tabHeader}</Tab>)}
            </TabList>
            <TabPanels>
              {tabData.map((item) => <TabPanel key={item.tabHeader}>{item.tabPanelContent}</TabPanel>)}
            </TabPanels>
          </Tabs>
        </PageContent>
      </PageViewport>
      <ApproveRequestAccessModal
        visible={approveModalVisible}
        onDismiss={onDismissApproveModal}
        onFinalize={onFinalizeApprove}
        request={approvedRequest}
        workspacesList={workspacesList}
      />
    </PageWrapper>
  );
};

AdminRequestAccess.propTypes = {
  match: PropTypes.object,
  toDoTabLoaded: PropTypes.bool,
  completeTabLoaded: PropTypes.bool,
  requests: PropTypes.arrayOf(PropTypes.shape({
    pearsonId: PropTypes.string.isRequired,
    region: PropTypes.string,
    workspace: PropTypes.string,
    manager: PropTypes.string,
    reason: PropTypes.string,
    email: PropTypes.string,
    status: PropTypes.oneOf(REQUEST_ACCESS_STATUS_VALUES),
    createdDate: PropTypes.string,
    lastModifiedDate: PropTypes.string,
  })),
  getWorkspaces: PropTypes.func.isRequired,
  getRequestAccessList: PropTypes.func.isRequired,
  workspacesList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  grantPermission: PropTypes.func.isRequired,
};

AdminRequestAccess.defaultProps = {
  requests: [],
};

const mapStateToProps = (state) => ({
  requests: selectRequestAccessRequests(state),
  toDoTabLoaded: selectToDoTabLoaded(state),
  completeTabLoaded: selectCompleteTabLoaded(state),
  workspacesList: selectWorkspacesList(state),
});

const mapDispatchToProps = (dispatch) => ({
  getWorkspaces: () => dispatch(commandWorkspaceWorkspacesList()),
  getRequestAccessList: () => {
    dispatch(setRequestAccessLoaded({ review: false, complete: false, deny: false }));
    dispatch(commandIamAccessRequestList('REVIEW'));
    dispatch(commandIamAccessRequestList('COMPLETE'));
    dispatch(commandIamAccessRequestList('DENY'));
  },
  grantPermission: (accountOrTeamIds, collaboratorType, workspaceId, permissionLevel) =>
    dispatch(commandWorkspaceGrantPermission(accountOrTeamIds, collaboratorType, workspaceId, permissionLevel)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminRequestAccess);
